import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { login } from '../redux/slices/authSlice';
import Login from '../components/Login';

const Register = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const redirectPath = location.state?.redirectPath || '/';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const fetchingStatesRef = useRef(false);
  const initialLoadRef = useRef(true);
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    country: '1', // Default to United States
    zipcode: '',
    password: '',
    confirmPassword: '',
    preferredContactMethod: 'Email'
  });

  const [validationErrors, setValidationErrors] = useState({});

  // Fetch countries on component mount
  useEffect(() => {
    fetchCountries();
  }, []);

  // Fetch states when country changes
  useEffect(() => {
    if (formData.country && !fetchingStatesRef.current) {
      fetchStatesByCountry(formData.country);
    } else if (!formData.country) {
      setStates([]);
    }
  }, [formData.country]);

  const fetchCountries = async () => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';
      const response = await fetch(`${apiBaseUrl}/CommonChannel/GetCountries`);
      if (response.ok) {
        const data = await response.json();
        setCountries(data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStatesByCountry = async (countryId) => {
    if (fetchingStatesRef.current) return;
    
    fetchingStatesRef.current = true;
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';
      const response = await fetch(`${apiBaseUrl}/CommonChannel/GetStatesByCountry?countryId=${countryId}`);
      if (response.ok) {
        const data = await response.json();
        setStates(data || []);
        
        // Set Georgia as default state only on initial load for United States
        if (countryId === '1' && initialLoadRef.current && data && data.length > 0) {
          const georgia = data.find(state => 
            (state.stateName && state.stateName.toLowerCase().includes('georgia')) ||
            (state.state_Name && state.state_Name.toLowerCase().includes('georgia'))
          );
          if (georgia) {
            // Use stateName as value since stateId is 0 for all states
            const stateName = georgia.stateName || georgia.state_Name || '';
            setFormData(prev => ({
              ...prev,
              state: stateName
            }));
          }
          initialLoadRef.current = false;
        }
      } else {
        setStates([]);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      setStates([]);
    } finally {
      fetchingStatesRef.current = false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // If country changes, reset state selection
    if (name === 'country') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        state: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateField = (name, value) => {
    let error = '';

    switch (name) {
      case 'firstName':
        if (!value.trim()) error = 'First name is required';
        else if (value.trim().length < 2) error = 'First name must be at least 2 characters';
        break;
      
      case 'lastName':
        if (!value.trim()) error = 'Last name is required';
        else if (value.trim().length < 2) error = 'Last name must be at least 2 characters';
        break;
      
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!value.trim()) error = 'Email is required';
        else if (!emailRegex.test(value)) error = 'Please enter a valid email address';
        break;
      
      case 'phone':
        // Enhanced phone validation - accepts various formats
        const phoneDigits = value.replace(/\D/g, '');
        if (!value.trim()) {
          error = 'Phone number is required';
        } else if (phoneDigits.length !== 10) {
          error = 'Phone number must be exactly 10 digits';
        } else if (!/^[2-9]\d{9}$/.test(phoneDigits)) {
          error = 'Please enter a valid US phone number';
        }
        break;
      
      case 'address1':
        // Address is now optional (hidden field)
        if (value.trim() && value.trim().length < 5) error = 'Please enter a complete address';
        break;
      
      case 'city':
        // City is now optional (hidden field) - only validate if provided
        if (value.trim() && value.trim().length < 2) {
          error = 'City name must be at least 2 characters';
        } else if (value.trim() && !/^[a-zA-Z\s\-'.]+$/.test(value.trim())) {
          error = 'City name can only contain letters, spaces, hyphens, and apostrophes';
        } else if (value.trim() && value.trim().length > 50) {
          error = 'City name is too long';
        }
        break;
      
      case 'state':
        // State is now optional (hidden field)
        break;
      
      case 'country':
        // Country is now optional (hidden field)
        break;
      
      case 'zipcode':
        // Zipcode is now optional (hidden field) - only validate if provided
        if (value.trim()) {
          const zipcodeDigits = value.replace(/\D/g, '');
          if (zipcodeDigits.length === 5) {
            if (!/^\d{5}$/.test(value.trim())) {
              error = 'Zip code must be 5 digits (e.g., 30328)';
            }
          } else if (zipcodeDigits.length === 9) {
            if (!/^\d{5}-?\d{4}$/.test(value.trim())) {
              error = 'Zip code must be in format 12345 or 12345-6789';
            }
          } else {
            error = 'Zip code must be 5 digits (e.g., 30328) or 9 digits (e.g., 30328-1234)';
          }
        }
        break;
      
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 6) error = 'Password must be at least 6 characters';
        break;
      
      case 'confirmPassword':
        if (!value) error = 'Please confirm your password';
        else if (value !== formData.password) error = 'Passwords do not match';
        break;
      
      default:
        break;
    }

    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    
    if (error) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Only validate required fields (not address fields which are now hidden)
    const requiredFields = ['firstName', 'lastName', 'email', 'phone', 'password', 'confirmPassword'];
    
    requiredFields.forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        errors[key] = error;
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      setError('Please fix all validation errors before submitting.');
      return;
    }

    setLoading(true);

    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/ykrestapi';

      // Find country name from countries array
      const selectedCountry = countries.find(c => 
        String(c.country_Id || c.countryId) === String(formData.country)
      );
      const countryName = selectedCountry ? (selectedCountry.country_Name || selectedCountry.countryName) : '';

      const registrationData = {
        user_First_Name: formData.firstName,
        user_Last_Name: formData.lastName,
        user_Email: formData.email,
        user_Phone: formData.phone,
        user_Cell_Phone_No: formData.phone,
        user_Home_Phone_No: formData.phone,
        user_Address_Line1: formData.address1,
        user_Address_Line2: formData.address2 || '',
        user_City: formData.city,
        user_State: formData.state, // State name (e.g., "Georgia")
        user_Country: countryName, // Country name (e.g., "United States")
        userStateId: '',
        countryId: '',
        user_Zipcode: formData.zipcode,
        user_Preferred_Contact_Method: formData.preferredContactMethod,
        user_Password: formData.password,
        createdBy: 'System',
        created: new Date().toISOString(),
        modifiedBy: 'System',
        modified: new Date().toISOString(),
        status: 'Y',
        // Required fields with default values
        stateSortCode: '',
        countrySortCode: '',
        user_Interested_In_Gurukul: 'N',
        user_Interested_In_Classes: 'Y',
        user_Other_State: '',
        user_Other_Country: '',
        is_Guest: 'N',
        baseUrl: '',
        apiKey: '',
        apiUserID: '',
        studentId: '',
        studentName: '',
        mode: '',
        customerProfileId: '',
        customerPaymentProfileId: '',
        hyc_customerProfileId: '',
        hyc_customerPaymentProfileId: '',
        rms_customerProfileId: '',
        rms_customerPaymentProfileId: ''
      };

      console.log('Registration data being sent:', {
        ...registrationData,
        user_Password: '***' // Hide password in logs
      });

      const response = await fetch(`${apiBaseUrl}/CommonChannel/InsertUserRegistration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registrationData)
      });

      console.log('Registration response status:', response.status);
      console.log('Registration response ok:', response.ok);

      if (response.ok) {
        const responseData = await response.text();
        console.log('Registration success response:', responseData);
        
        // Auto-login after successful registration
        try {
          console.log('Attempting auto-login after registration...');
          const loginResponse = await fetch(`${apiBaseUrl}/CommonChannel/ValidateUserDirect`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              Email: formData.email,
              Password: formData.password
            }),
          });

          if (loginResponse.ok && loginResponse.status !== 204) {
            const userData = await loginResponse.json();
            console.log('Auto-login successful:', userData);
            
            // Dispatch login to Redux store
            dispatch(login({ 
              email: formData.email, 
              entity: 'YK', 
              userData: userData 
            }));
            
            setSuccess(true);
            setTimeout(() => {
              navigate(redirectPath);
            }, 2000);
          } else {
            console.warn('Auto-login failed, showing success message');
            setSuccess(true);
            setTimeout(() => {
              navigate(redirectPath);
            }, 2000);
          }
        } catch (loginError) {
          console.error('Auto-login error:', loginError);
          // Still show success, user can login manually
          setSuccess(true);
          setTimeout(() => {
            navigate(redirectPath);
          }, 2000);
        }
      } else {
        const errorData = await response.json();
        console.error('Registration error response:', errorData);
        setError(errorData.message || 'Registration failed. Please try again.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      setError(`An error occurred during registration: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="mb-4">
            <svg className="w-16 h-16 text-green-500 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Registration Successful!</h2>
          <p className="text-gray-600 mb-4">Your account has been created successfully.</p>
          <p className="text-sm text-gray-500">Redirecting to home page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-5xl w-full">
        {/* Compact Registration Form */}
        <div className="bg-white rounded-2xl shadow-[0_20px_70px_rgba(0,0,0,0.08)] border border-gray-100 p-6 relative">
          {/* Black border at top like login */}
          <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-black to-transparent"></div>
          
          {/* Compact Header inside form */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-black rounded-xl shadow-lg mb-2">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">Create an Account</h1>
            <p className="text-xs text-gray-600">Join us today</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Personal Information Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Personal Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* First Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all duration-200 text-sm ${
                    validationErrors.firstName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="First name"
                />
                {validationErrors.firstName && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.firstName}</p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.lastName
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="Last name"
                />
                {validationErrors.lastName && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.lastName}</p>
                )}
              </div>

              </div>
            </div>

            {/* Contact Information Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Contact Information
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                      validationErrors.email
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                    }`}
                    placeholder="email@example.com"
                  />
                  {validationErrors.email && (
                    <p className="text-red-500 text-xs mt-0.5">{validationErrors.email}</p>
                  )}
                </div>

                {/* Phone */}
                <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.phone
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="(123) 456-7890"
                />
                {validationErrors.phone && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.phone}</p>
                )}
              </div>

              </div>
            </div>

            {/* Address Information Section - Hidden but kept in state */}

            {/* Security Section */}
            <div>
              <h3 className="text-sm font-bold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                Security
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Password */}
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                      validationErrors.password
                        ? 'border-red-500 focus:border-red-500'
                        : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                    }`}
                    placeholder="Password"
                  />
                  {validationErrors.password && (
                    <p className="text-red-500 text-xs mt-0.5">{validationErrors.password}</p>
                  )}
                </div>

                {/* Confirm Password */}
                <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Confirm Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition text-sm ${
                    validationErrors.confirmPassword
                      ? 'border-red-500 focus:border-red-500'
                      : 'border-gray-200 focus:border-black focus:ring-1 focus:ring-gray-100'
                  }`}
                  placeholder="Confirm password"
                />
                {validationErrors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-0.5">{validationErrors.confirmPassword}</p>
                )}
              </div>
            </div>
          </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Submit Buttons - Minimalist */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowLogin(true)}
                disabled={loading}
                className="flex-1 bg-white border-2 border-gray-200 text-gray-700 font-semibold py-3 px-6 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-black text-white font-bold py-3 px-6 rounded-xl hover:bg-gray-800 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Creating...
                  </span>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            {/* Login Link */}
            <div className="text-center pt-2 border-t border-gray-200">
              <p className="text-gray-600 text-sm">
                Already have an account?{' '}
                <button
                  type="button"
                  onClick={() => setShowLogin(true)}
                  className="text-black hover:underline font-semibold"
                >
                  Login here
                </button>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Login Modal */}
      {showLogin && (
        <Login
          onLoginSuccess={(data) => {
            // Close modal and navigate to redirectPath
            setShowLogin(false);
            navigate(redirectPath);
          }}
          onCancel={() => setShowLogin(false)}
          entity="RMS"
          redirectPath={redirectPath}
        />
      )}
    </div>
  );
};

export default Register;
